import {
  // Internal Tab Action Types
  GET_INTERNAL_POSITIONS_REQUEST,
  GET_INTERNAL_POSITIONS_SUCCESS,
  GET_INTERNAL_POSITIONS_FAILURE,
  CREATE_INTERNAL_POSITION_REQUEST,
  CREATE_INTERNAL_POSITION_SUCCESS,
  CREATE_INTERNAL_POSITION_FAILURE,
  UPDATE_INTERNAL_POSITION_REQUEST,
  UPDATE_INTERNAL_POSITION_SUCCESS,
  UPDATE_INTERNAL_POSITION_FAILURE,
  DELETE_INTERNAL_POSITION_REQUEST,
  DELETE_INTERNAL_POSITION_SUCCESS,
  DELETE_INTERNAL_POSITION_FAILURE,
  
  // External Tab Action Types
  GET_EXTERNAL_POSITIONS_REQUEST,
  GET_EXTERNAL_POSITIONS_SUCCESS,
  GET_EXTERNAL_POSITIONS_FAILURE,
  CREATE_EXTERNAL_POSITION_REQUEST,
  CREATE_EXTERNAL_POSITION_SUCCESS,
  CREATE_EXTERNAL_POSITION_FAILURE,
  UPDATE_EXTERNAL_POSITION_REQUEST,
  UPDATE_EXTERNAL_POSITION_SUCCESS,
  UPDATE_EXTERNAL_POSITION_FAILURE,
  DELETE_EXTERNAL_POSITION_REQUEST,
  DELETE_EXTERNAL_POSITION_SUCCESS,
  DELETE_EXTERNAL_POSITION_FAILURE,
  
  // Units Tab Action Types
  GET_UNITS_REQUEST,
  GET_UNITS_SUCCESS,
  GET_UNITS_FAILURE,
  CREATE_UNIT_REQUEST,
  CREATE_UNIT_SUCCESS,
  CREATE_UNIT_FAILURE,
  UPDATE_UNIT_REQUEST,
  UPDATE_UNIT_SUCCESS,
  UPDATE_UNIT_FAILURE,
  DELETE_UNIT_REQUEST,
  DELETE_UNIT_SUCCESS,
  DELETE_UNIT_FAILURE,
  
  // Clients Tab Action Types
  GET_CLIENTS_REQUEST,
  GET_CLIENTS_SUCCESS,
  GET_CLIENTS_FAILURE,
  CREATE_CLIENT_REQUEST,
  CREATE_CLIENT_SUCCESS,
  CREATE_CLIENT_FAILURE,
  UPDATE_CLIENT_REQUEST,
  UPDATE_CLIENT_SUCCESS,
  UPDATE_CLIENT_FAILURE,
  DELETE_CLIENT_REQUEST,
  DELETE_CLIENT_SUCCESS,
  DELETE_CLIENT_FAILURE,
  
  // Suppliers Tab Action Types
  GET_SUPPLIERS_REQUEST,
  GET_SUPPLIERS_SUCCESS,
  GET_SUPPLIERS_FAILURE,
  CREATE_SUPPLIER_REQUEST,
  CREATE_SUPPLIER_SUCCESS,
  CREATE_SUPPLIER_FAILURE,
  UPDATE_SUPPLIER_REQUEST,
  UPDATE_SUPPLIER_SUCCESS,
  UPDATE_SUPPLIER_FAILURE,
  DELETE_SUPPLIER_REQUEST,
  DELETE_SUPPLIER_SUCCESS,
  DELETE_SUPPLIER_FAILURE,
  
  // Unit Budget Usage Action Types
  GET_UNIT_BUDGET_USAGE_REQUEST,
  GET_UNIT_BUDGET_USAGE_SUCCESS,
  GET_UNIT_BUDGET_USAGE_FAILURE,

  // Categories Tab Action Types
  GET_CATEGORIES_REQUEST,
  GET_CATEGORIES_SUCCESS,
  GET_CATEGORIES_FAILURE,
  CREATE_CATEGORY_REQUEST,
  CREATE_CATEGORY_SUCCESS,
  CREATE_CATEGORY_FAILURE,
  UPDATE_CATEGORY_REQUEST,
  UPDATE_CATEGORY_SUCCESS,
  UPDATE_CATEGORY_FAILURE,
  DELETE_CATEGORY_REQUEST,
  DELETE_CATEGORY_SUCCESS,
  DELETE_CATEGORY_FAILURE,

  IBudgetSettingsAction
} from '../actions/budgetSettingsActions'

import {
  IInternalPosition,
  IExternalPosition,
  IUnit,
  IUnitBudgetUsage,
  IClient,
  ISupplier,
  IBudgetSettingsCategory
} from '../../../api/budgetSettingsApi'

// State interfaces
interface ITabState<T> {
  loading: boolean
  error: string | null
}

interface IInternalState extends ITabState<IInternalPosition> {
  positions: IInternalPosition[]
}

interface IExternalState extends ITabState<IExternalPosition> {
  positions: IExternalPosition[]
}

interface IUnitsState extends ITabState<IUnit> {
  units: IUnit[]
}

interface IClientsState extends ITabState<IClient> {
  clients: IClient[]
}

interface ISuppliersState extends ITabState<ISupplier> {
  suppliers: ISupplier[]
}

interface ICategoriesState extends ITabState<IBudgetSettingsCategory> {
  categories: IBudgetSettingsCategory[]
}

interface IUnitBudgetUsageState {
  data: IUnitBudgetUsage
  loading: boolean
  error: string | null
}

export interface IBudgetSettingsState {
  internal: IInternalState
  external: IExternalState
  units: IUnitsState
  unitBudgetUsage: IUnitBudgetUsageState
  clients: IClientsState
  suppliers: ISuppliersState
  categories: ICategoriesState
}

export const initialState: IBudgetSettingsState = {
  internal: {
    positions: [],
    loading: false,
    error: null
  },
  external: {
    positions: [],
    loading: false,
    error: null
  },
  units: {
    units: [],
    loading: false,
    error: null
  },
  unitBudgetUsage: {
    data: {},
    loading: false,
    error: null
  },
  clients: {
    clients: [],
    loading: false,
    error: null
  },
  suppliers: {
    suppliers: [],
    loading: false,
    error: null
  },
  categories: {
    categories: [],
    loading: false,
    error: null
  }
}

export const budgetSettingsReducer = (state = initialState, action: IBudgetSettingsAction): IBudgetSettingsState => {
  switch (action.type) {
    // Internal Positions Actions
    case GET_INTERNAL_POSITIONS_REQUEST:
    case CREATE_INTERNAL_POSITION_REQUEST:
    case UPDATE_INTERNAL_POSITION_REQUEST:
    case DELETE_INTERNAL_POSITION_REQUEST:
      return {
        ...state,
        internal: {
          ...state.internal,
          loading: true,
          error: null
        }
      }
    
    case GET_INTERNAL_POSITIONS_SUCCESS:
      return {
        ...state,
        internal: {
          positions: Array.isArray(action.payload) 
            ? action.payload 
            : action.payload.results,
          loading: false,
          error: null
        }
      }
    
    case CREATE_INTERNAL_POSITION_SUCCESS:
      return {
        ...state,
        internal: {
          positions: [action.payload, ...state.internal.positions],
          loading: false,
          error: null
        }
      }
    
    case UPDATE_INTERNAL_POSITION_SUCCESS:
      return {
        ...state,
        internal: {
          positions: state.internal.positions.map(position =>
            position.id === action.payload.id ? action.payload : position
          ),
          loading: false,
          error: null
        }
      }
    
    case DELETE_INTERNAL_POSITION_SUCCESS:
      return {
        ...state,
        internal: {
          positions: state.internal.positions.filter(position => position.id !== action.payload),
          loading: false,
          error: null
        }
      }
    
    case GET_INTERNAL_POSITIONS_FAILURE:
    case CREATE_INTERNAL_POSITION_FAILURE:
    case UPDATE_INTERNAL_POSITION_FAILURE:
    case DELETE_INTERNAL_POSITION_FAILURE:
      return {
        ...state,
        internal: {
          ...state.internal,
          loading: false,
          error: action.payload
        }
      }

    // External Positions Actions
    case GET_EXTERNAL_POSITIONS_REQUEST:
    case CREATE_EXTERNAL_POSITION_REQUEST:
    case UPDATE_EXTERNAL_POSITION_REQUEST:
    case DELETE_EXTERNAL_POSITION_REQUEST:
      return {
        ...state,
        external: {
          ...state.external,
          loading: true,
          error: null
        }
      }
    
    case GET_EXTERNAL_POSITIONS_SUCCESS:
      return {
        ...state,
        external: {
          positions: Array.isArray(action.payload) 
            ? action.payload 
            : action.payload.results,
          loading: false,
          error: null
        }
      }
    
    case CREATE_EXTERNAL_POSITION_SUCCESS:
      return {
        ...state,
        external: {
          positions: [action.payload, ...state.external.positions],
          loading: false,
          error: null
        }
      }
    
    case UPDATE_EXTERNAL_POSITION_SUCCESS:
      return {
        ...state,
        external: {
          positions: state.external.positions.map(position =>
            position.id === action.payload.id ? action.payload : position
          ),
          loading: false,
          error: null
        }
      }
    
    case DELETE_EXTERNAL_POSITION_SUCCESS:
      return {
        ...state,
        external: {
          positions: state.external.positions.filter(position => position.id !== action.payload),
          loading: false,
          error: null
        }
      }
    
    case GET_EXTERNAL_POSITIONS_FAILURE:
    case CREATE_EXTERNAL_POSITION_FAILURE:
    case UPDATE_EXTERNAL_POSITION_FAILURE:
    case DELETE_EXTERNAL_POSITION_FAILURE:
      return {
        ...state,
        external: {
          ...state.external,
          loading: false,
          error: action.payload
        }
      }

    // Units Actions
    case GET_UNITS_REQUEST:
    case CREATE_UNIT_REQUEST:
    case UPDATE_UNIT_REQUEST:
    case DELETE_UNIT_REQUEST:
      return {
        ...state,
        units: {
          ...state.units,
          loading: true,
          error: null
        }
      }
    
    case GET_UNITS_SUCCESS:
      return {
        ...state,
        units: {
          units: Array.isArray(action.payload) 
            ? action.payload 
            : action.payload.results,
          loading: false,
          error: null
        }
      }
    
    case CREATE_UNIT_SUCCESS:
      return {
        ...state,
        units: {
          units: [action.payload, ...state.units.units],
          loading: false,
          error: null
        }
      }
    
    case UPDATE_UNIT_SUCCESS:
      return {
        ...state,
        units: {
          units: state.units.units.map(unit =>
            unit.id === action.payload.id ? action.payload : unit
          ),
          loading: false,
          error: null
        }
      }
    
    case DELETE_UNIT_SUCCESS:
      return {
        ...state,
        units: {
          units: state.units.units.filter(unit => unit.id !== action.payload),
          loading: false,
          error: null
        }
      }
    
    case GET_UNITS_FAILURE:
    case CREATE_UNIT_FAILURE:
    case UPDATE_UNIT_FAILURE:
    case DELETE_UNIT_FAILURE:
      return {
        ...state,
        units: {
          ...state.units,
          loading: false,
          error: action.payload
        }
      }

    // Unit Budget Usage Actions
    case GET_UNIT_BUDGET_USAGE_REQUEST:
      return {
        ...state,
        unitBudgetUsage: {
          ...state.unitBudgetUsage,
          loading: true,
          error: null
        }
      }

    case GET_UNIT_BUDGET_USAGE_SUCCESS:
      return {
        ...state,
        unitBudgetUsage: {
          data: action.payload,
          loading: false,
          error: null
        }
      }

    case GET_UNIT_BUDGET_USAGE_FAILURE:
      return {
        ...state,
        unitBudgetUsage: {
          ...state.unitBudgetUsage,
          loading: false,
          error: action.payload
        }
      }

    // Clients Actions
    case GET_CLIENTS_REQUEST:
    case CREATE_CLIENT_REQUEST:
    case UPDATE_CLIENT_REQUEST:
    case DELETE_CLIENT_REQUEST:
      return {
        ...state,
        clients: {
          ...state.clients,
          loading: true,
          error: null
        }
      }
    
    case GET_CLIENTS_SUCCESS:
      return {
        ...state,
        clients: {
          clients: Array.isArray(action.payload) 
            ? action.payload 
            : action.payload.results,
          loading: false,
          error: null
        }
      }
    
    case CREATE_CLIENT_SUCCESS:
      return {
        ...state,
        clients: {
          clients: [action.payload, ...state.clients.clients],
          loading: false,
          error: null
        }
      }
    
    case UPDATE_CLIENT_SUCCESS:
      return {
        ...state,
        clients: {
          clients: state.clients.clients.map(client =>
            client.id === action.payload.id ? action.payload : client
          ),
          loading: false,
          error: null
        }
      }
    
    case DELETE_CLIENT_SUCCESS:
      return {
        ...state,
        clients: {
          clients: state.clients.clients.filter(client => client.id !== action.payload),
          loading: false,
          error: null
        }
      }
    
    case GET_CLIENTS_FAILURE:
    case CREATE_CLIENT_FAILURE:
    case UPDATE_CLIENT_FAILURE:
    case DELETE_CLIENT_FAILURE:
      return {
        ...state,
        clients: {
          ...state.clients,
          loading: false,
          error: action.payload
        }
      }

    // Suppliers Actions
    case GET_SUPPLIERS_REQUEST:
    case CREATE_SUPPLIER_REQUEST:
    case UPDATE_SUPPLIER_REQUEST:
    case DELETE_SUPPLIER_REQUEST:
      return {
        ...state,
        suppliers: {
          ...state.suppliers,
          loading: true,
          error: null
        }
      }
    
    case GET_SUPPLIERS_SUCCESS:
      return {
        ...state,
        suppliers: {
          suppliers: Array.isArray(action.payload) 
            ? action.payload 
            : action.payload.results,
          loading: false,
          error: null
        }
      }
    
    case CREATE_SUPPLIER_SUCCESS:
      return {
        ...state,
        suppliers: {
          suppliers: [action.payload, ...state.suppliers.suppliers],
          loading: false,
          error: null
        }
      }
    
    case UPDATE_SUPPLIER_SUCCESS:
      return {
        ...state,
        suppliers: {
          suppliers: state.suppliers.suppliers.map(supplier =>
            supplier.id === action.payload.id ? action.payload : supplier
          ),
          loading: false,
          error: null
        }
      }
    
    case DELETE_SUPPLIER_SUCCESS:
      return {
        ...state,
        suppliers: {
          suppliers: state.suppliers.suppliers.filter(supplier => supplier.id !== action.payload),
          loading: false,
          error: null
        }
      }
    
    case GET_SUPPLIERS_FAILURE:
    case CREATE_SUPPLIER_FAILURE:
    case UPDATE_SUPPLIER_FAILURE:
    case DELETE_SUPPLIER_FAILURE:
      return {
        ...state,
        suppliers: {
          ...state.suppliers,
          loading: false,
          error: action.payload
        }
      }

    // Categories Actions
    case GET_CATEGORIES_REQUEST:
      return {
        ...state,
        categories: {
          ...state.categories,
          loading: true,
          error: null
        }
      }

    case GET_CATEGORIES_SUCCESS:
      return {
        ...state,
        categories: {
          ...state.categories,
          categories: action.payload,
          loading: false,
          error: null
        }
      }

    case GET_CATEGORIES_FAILURE:
      return {
        ...state,
        categories: {
          ...state.categories,
          loading: false,
          error: action.payload
        }
      }

    case CREATE_CATEGORY_REQUEST:
    case UPDATE_CATEGORY_REQUEST:
    case DELETE_CATEGORY_REQUEST:
      return {
        ...state,
        categories: {
          ...state.categories,
          loading: true
        }
      }

    case CREATE_CATEGORY_SUCCESS:
      return {
        ...state,
        categories: {
          ...state.categories,
          categories: [...state.categories.categories, action.payload],
          loading: false,
          error: null
        }
      }

    case UPDATE_CATEGORY_SUCCESS:
      return {
        ...state,
        categories: {
          ...state.categories,
          categories: state.categories.categories.map(category =>
            category.id === action.payload.id ? action.payload : category
          ),
          loading: false,
          error: null
        }
      }

    case DELETE_CATEGORY_SUCCESS:
      return {
        ...state,
        categories: {
          ...state.categories,
          categories: state.categories.categories.filter(category => category.id !== action.payload),
          loading: false,
          error: null
        }
      }

    case CREATE_CATEGORY_FAILURE:
    case UPDATE_CATEGORY_FAILURE:
    case DELETE_CATEGORY_FAILURE:
      return {
        ...state,
        categories: {
          ...state.categories,
          loading: false,
          error: action.payload
        }
      }

    default:
      return state
  }
} 